import { addDays, set } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { CurrencyExchangePeriodResponse } from '../../resources';
import { BudapestTimezone, getDateTimeInHoursAndMinuteAtBudapestTZ } from '../../utils';

/**
 * Result of the actual-exchange-period query, as a consistent object shape for both outcomes so
 * callers never have to special-case `undefined`/`null`. The backend returns 204 No Content when
 * there is no exchange period at all right now, which is represented here as `hasPeriod: false`.
 */
export type ExchangePeriodQueryResult =
  | { hasPeriod: true; period: CurrencyExchangePeriodResponse }
  | { hasPeriod: false };

const toMinutesOfDay = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * True when `now` falls inside the period's [start, end) window, evaluated in Budapest time.
 * Handles a window that wraps past midnight (end < start). Always false when there is no period.
 */
export const isExchangePeriodActive = (result: ExchangePeriodQueryResult, now: Date): boolean => {
  if (!result.hasPeriod) {
    return false;
  }

  const [nowHours, nowMinutes] = getDateTimeInHoursAndMinuteAtBudapestTZ(now);
  const nowMinutesOfDay = nowHours * 60 + nowMinutes;
  const start = toMinutesOfDay(result.period.startTimeString);
  const end = toMinutesOfDay(result.period.endTimeString);

  return start <= end
    ? nowMinutesOfDay >= start && nowMinutesOfDay < end
    : nowMinutesOfDay >= start || nowMinutesOfDay < end;
};

const budapestTimeToInstant = (budapestNow: Date, time: string, addDaysCount: number): Date => {
  const [hours, minutes] = time.split(':').map(Number);
  return fromZonedTime(
    set(addDays(budapestNow, addDaysCount), { hours, minutes, seconds: 0, milliseconds: 0 }),
    BudapestTimezone,
  );
};

/** Grace given to the backend to switch over before the new window's conditions are asked for. */
const exchangePeriodRecheckDelayMs = 3000;

/**
 * The next instant at which the exchange-period condition should be re-checked, in Budapest time (DST-correct):
 * - while active → the end of the current window (when it closes);
 * - while inactive → the next occurrence of the window start (when it opens).
 * The boundary itself is never the answer — the delay above is always added to it, so a boundary that has
 * just passed still gets its late check today instead of being deferred to tomorrow's occurrence.
 * Returns `undefined` when there is no period (204 No Content) — there is no window boundary to schedule against.
 */
export const getNextExchangePeriodCheckAt = (result: ExchangePeriodQueryResult, now: Date): Date | undefined => {
  if (!result.hasPeriod) {
    return undefined;
  }

  const budapestNow = toZonedTime(now, BudapestTimezone);
  const targetTime = isExchangePeriodActive(result, now) ? result.period.endTimeString : result.period.startTimeString;
  const toCheckAt = (addDaysCount: number): Date =>
    new Date(budapestTimeToInstant(budapestNow, targetTime, addDaysCount).getTime() + exchangePeriodRecheckDelayMs);
  const todayCheckAt = toCheckAt(0);

  return todayCheckAt.getTime() > now.getTime() ? todayCheckAt : toCheckAt(1);
};
